import { Link, useLocation } from "wouter";
import { useClerk, useUser } from "@clerk/react";
import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { useGetCurrentUser } from "@workspace/api-client-react";

import { useRunnerView } from "@/hooks/useRunnerView";

const coachNavItems = [
  { href: "/dashboard", label: "Dashboard" },
  { href: "/squads", label: "Squads" },
  { href: "/members", label: "Members" },
];

// WI#23 (D4): the runner app — Today · My History · My Squad.
function runnerNavItems(squadId: number | null | undefined) {
  return [
    { href: "/today", label: "Today" },
    { href: "/history", label: "My History" },
    ...(squadId ? [{ href: `/squads/${squadId}`, label: "My Squad" }] : []),
  ];
}

function UserMenu() {
  const { signOut } = useClerk();
  const { user } = useUser();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // WI#23 — sign-up doesn't collect a name, so fall back to the name the runner gave their squad
  // profile before the generic "Runner".
  const { data: currentUser } = useGetCurrentUser();
  const rosterName = currentUser?.members?.[0]?.name;
  const displayName = user?.firstName
    ? `${user.firstName}${user.lastName ? " " + user.lastName : ""}`
    : user?.username ?? rosterName ?? "Runner";
  const initials = displayName === "Runner"
    ? "?"
    : displayName.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-100 transition-colors"
      >
        <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold uppercase select-none">
          {initials}
        </div>
        <span className="text-sm font-medium text-foreground hidden sm:block">
          {displayName}
        </span>
        <svg className="w-3.5 h-3.5 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-44 bg-background border border-border rounded shadow-md z-50 py-1">
          <div className="px-3 py-2 border-b border-border">
            <p className="text-xs font-semibold text-foreground truncate">{displayName}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.primaryEmailAddress?.emailAddress}</p>
          </div>
          <button
            onClick={() => signOut()}
            className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-zinc-50 transition-colors"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const { isRunnerView, me } = useRunnerView();
  const [location] = useLocation();

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="border-b border-border sticky top-0 z-50 bg-background/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <img
              src={`${import.meta.env.BASE_URL}logo.svg`}
              alt="PeakRunning"
              className="h-6 w-auto"
            />
          </Link>
          <div className="flex items-center gap-3">
            <nav className="flex items-center gap-1">
              {(isRunnerView ? runnerNavItems(me?.squadId) : coachNavItems).map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    "px-3 py-1.5 rounded text-sm font-medium transition-colors",
                    location === item.href || location.startsWith(item.href + "/")
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
            <UserMenu />
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-8">
        {children}
      </main>
    </div>
  );
}
